import { isUndefined } from 'lodash-es';

import type { MinMax } from '../../core/types/min-max.js';
import {
  binarySearch,
  boundariesOfCluster,
} from '../../core/utils/binary-search.js';
import { isBandScale } from '../../core/utils/detect-scale.js';
import type { RectanglePoint } from '../components/overlay/explore/types.js';
import { isRectangleSelected } from '../components/overlay/utils/is-rectangle-selected.js';
import { AxisIdToScales } from '../context/XYChartScales.context.js';
import {
  FlameDatapointInternal,
  FlameSeriesWithDatapoints,
  RectDatapointInternal,
  RectSeriesWithDatapoints,
  type AxisIdToScaleDomain,
  type BarSeriesWithDatapoints,
} from '../types/xy-chart-internals.js';
import { XYAccessorDataTypes, type BarDatapoint } from '../types/xy-chart.js';
import { getCurrentScale } from '../utils/get-scale.js';
import { toAbsolute } from '../utils/value-representation.js';

export interface XYChartSelectionRectangleMetadata {
  series:
    | RectSeriesWithDatapoints
    | BarSeriesWithDatapoints
    | FlameSeriesWithDatapoints;
  datapoint: RectDatapointInternal | BarDatapoint | FlameDatapointInternal;
}

export const isValidNumeric = (v?: XYAccessorDataTypes) =>
  v !== undefined && typeof v !== 'string';

const toNumeric = (v: XYAccessorDataTypes): number =>
  v instanceof Date ? v.getTime() : (v as number);

const getColumnX0 = (column: NumericColumn) => column.x0;

export interface NumericColumn {
  x0: number;
  x1: number;
  rectangles: XYChartSelectionRectangleMetadata[];
}

export interface RectAxisSelectConfig {
  numericColumns: NumericColumn[];
  allRectangles: XYChartSelectionRectangleMetadata[];
  isHorizontal: boolean;
}

const findFirstOverlap = (
  columns: NumericColumn[],
  selectionMax: number,
): number => {
  if (columns.length === 0) {
    return 0;
  }
  const idx = binarySearch(selectionMax, columns, getColumnX0);
  if (columns[idx].x0 > selectionMax) {
    return idx;
  }
  const [, rightIndex] = boundariesOfCluster(idx, columns, getColumnX0);
  return rightIndex + 1;
};

export const useSortRectangleDatapoints = () => {
  const pending: XYChartSelectionRectangleMetadata[] = [];
  const rectAxisSelectConfig = new Map<string, RectAxisSelectConfig>();
  const numericLookup = new Map<
    string,
    Map<number, Map<number, NumericColumn>>
  >();
  let builtCount = 0;

  const handleAddValue = (rect: XYChartSelectionRectangleMetadata) => {
    pending.push(rect);
  };

  const getOrCreateConfig = (
    axisId: string,
    isHorizontal: boolean,
  ): RectAxisSelectConfig => {
    let config = rectAxisSelectConfig.get(axisId);
    if (config === undefined) {
      config = { numericColumns: [], allRectangles: [], isHorizontal };
      rectAxisSelectConfig.set(axisId, config);
    }
    return config;
  };

  const indexRect = (rect: XYChartSelectionRectangleMetadata) => {
    const { x0, x1, y0, y1 } = rect.datapoint;

    if (
      isUndefined(x0) &&
      isUndefined(x1) &&
      isUndefined(y0) &&
      isUndefined(y1)
    ) {
      return;
    }

    const xAxisId = rect.series.xAxisId;
    const yAxisId = rect.series.yAxisId;

    const isVerticalBar =
      typeof x0 === 'string' || (typeof y0 !== 'string' && !isUndefined(x0));
    const isHorizontalBar =
      typeof y0 === 'string' ||
      (typeof x0 !== 'string' && isUndefined(x0) && !isUndefined(y0));

    const primaryAxisId = isVerticalBar
      ? xAxisId
      : isHorizontalBar
        ? yAxisId
        : xAxisId;
    const primaryV0 = isVerticalBar ? x0 : isHorizontalBar ? y0 : x0;
    const primaryV1 = isVerticalBar ? x1 : isHorizontalBar ? y1 : x1;

    const config = getOrCreateConfig(primaryAxisId, isHorizontalBar);
    config.allRectangles.push(rect);

    if (typeof primaryV0 === 'string' || typeof primaryV1 === 'string') {
      return;
    }
    if (primaryV0 === undefined) {
      return;
    }

    const v0Num = toNumeric(primaryV0);
    const v1Num = primaryV1 !== undefined ? toNumeric(primaryV1) : v0Num;
    const [lo, hi] = v0Num <= v1Num ? [v0Num, v1Num] : [v1Num, v0Num];

    let perAxis = numericLookup.get(primaryAxisId);
    if (perAxis === undefined) {
      perAxis = new Map();
      numericLookup.set(primaryAxisId, perAxis);
    }
    let perX0 = perAxis.get(lo);
    if (perX0 === undefined) {
      perX0 = new Map();
      perAxis.set(lo, perX0);
    }
    let column = perX0.get(hi);
    if (column === undefined) {
      column = { x0: lo, x1: hi, rectangles: [] };
      perX0.set(hi, column);
      config.numericColumns.push(column);
    }
    column.rectangles.push(rect);
  };

  const ensureBuilt = () => {
    if (builtCount === pending.length) {
      return;
    }
    for (let i = builtCount; i < pending.length; i++) {
      indexRect(pending[i]);
    }
    builtCount = pending.length;
    for (const config of rectAxisSelectConfig.values()) {
      if (config.numericColumns.length > 1) {
        config.numericColumns.sort((a, b) => a.x0 - b.x0);
      }
    }
  };

  const filterNumericColumnsInRange = (
    numericColumns: NumericColumn[],
    start: XYAccessorDataTypes,
    end: XYAccessorDataTypes,
  ): NumericColumn[] => {
    const sX = start instanceof Date ? start.getTime() : Number(start);
    const eX = end instanceof Date ? end.getTime() : Number(end);
    const [min, max] = sX <= eX ? [sX, eX] : [eX, sX];
    const upper = findFirstOverlap(numericColumns, max);
    const result: NumericColumn[] = [];
    for (let i = 0; i < upper; i++) {
      const column = numericColumns[i];
      if (
        isRectangleSelected({
          x: {
            rectangleStart: column.x0,
            rectangleEnd: column.x1,
            selectionStart: min,
            selectionEnd: max,
          },
        })
      ) {
        result.push(column);
      }
    }
    return result;
  };

  const collectRectanglesByCallback = (
    rectangles: XYChartSelectionRectangleMetadata[],
    callback: (point: XYChartSelectionRectangleMetadata) => RectanglePoint,
    selectionStart: number,
    selectionEnd: number,
    out: RectanglePoint[],
  ) => {
    for (const rect of rectangles) {
      const rectanglePoint = callback(rect);
      if (!rectanglePoint) {
        continue;
      }
      const barStart = rectanglePoint.x;
      const barEnd = rectanglePoint.x + rectanglePoint.width;
      if (
        isRectangleSelected({
          x: {
            rectangleStart: barStart,
            rectangleEnd: barEnd,
            selectionStart,
            selectionEnd,
          },
        })
      ) {
        out.push(rectanglePoint);
      }
    }
  };

  const searchVerticalNumeric = (
    start: XYAccessorDataTypes,
    end: XYAccessorDataTypes,
    callback: (point: XYChartSelectionRectangleMetadata) => RectanglePoint,
    config: RectAxisSelectConfig,
  ): RectanglePoint[] => {
    if (config.numericColumns.length === 0) {
      return [];
    }
    const columnsInRange = filterNumericColumnsInRange(
      config.numericColumns,
      start,
      end,
    );
    const rectsByRange: RectanglePoint[] = [];
    for (const column of columnsInRange) {
      for (const rect of column.rectangles) {
        const mapped = callback(rect);
        if (mapped) {
          rectsByRange.push(mapped);
        }
      }
    }
    return rectsByRange;
  };

  const searchAllAxisDatapoints = (
    start: number,
    end: number,
    callback: (point: XYChartSelectionRectangleMetadata) => RectanglePoint,
    axesIds: string | string[],
    axisIdToScales: AxisIdToScales,
    axisIdToScaleDomain: AxisIdToScaleDomain,
    axisIDToIsRelative: Map<string, boolean>,
  ) => {
    ensureBuilt();

    const axesCurrentSelection: Record<string, RectanglePoint[]> = {};
    const highlightedDatapoints: RectanglePoint[] = [];

    if (!axisIdToScales) {
      return { axesCurrentSelection, highlightedDatapoints };
    }

    const axesIdsArray = typeof axesIds === 'string' ? [axesIds] : axesIds;
    const [selectionStart, selectionEnd] =
      start < end ? [start, end] : [end, start];

    for (const axisId of axesIdsArray) {
      const config = rectAxisSelectConfig.get(axisId);
      if (!config) {
        continue;
      }
      const currentScale = getCurrentScale(axisIdToScales, axisId);
      if (isBandScale(currentScale)) {
        collectRectanglesByCallback(
          config.allRectangles,
          callback,
          selectionStart,
          selectionEnd,
          highlightedDatapoints,
        );
        continue;
      }

      if (config.isHorizontal) {
        collectRectanglesByCallback(
          config.allRectangles,
          callback,
          selectionStart,
          selectionEnd,
          highlightedDatapoints,
        );
        continue;
      }

      let startDomain = currentScale.invert(start);
      let endDomain = currentScale.invert(Math.ceil(end));
      if (axisIDToIsRelative.get(axisId)) {
        const domain = axisIdToScaleDomain.get(axisId) as MinMax;
        startDomain = toAbsolute(startDomain as number, domain);
        endDomain = toAbsolute(endDomain as number, domain);
      }
      const seriesHighlightedDatapoints = searchVerticalNumeric(
        startDomain,
        endDomain,
        callback,
        config,
      );

      for (const datapoint of seriesHighlightedDatapoints) {
        highlightedDatapoints.push(datapoint);
      }
      axesCurrentSelection[axisId] = seriesHighlightedDatapoints;
    }

    return { axesCurrentSelection, highlightedDatapoints };
  };

  return {
    addValue: handleAddValue,
    getCurrentSelectConfig: () => {
      ensureBuilt();
      return rectAxisSelectConfig;
    },
    searchAllAxisDatapoints,
    hasDatapoints: () => pending.length > 0,
  };
};
